/**
 * One theme module for ECharts, replacing `company/theme/echartsTheme.ts`.
 *
 * ECharts renders to `<canvas>`, so it cannot resolve Fluent's `tokens.*`
 * CSS variable references (`var(--colorXyz)`) -- every color handed to
 * ECharts must be a real, already-resolved string. Per spec §3.1, hard-coded
 * color values (hex / `rgb()` / `rgba()`) are allowed in this file and only
 * this file; everywhere else obtains color from `useChartColors()` below.
 *
 * `TeamsAuthProvider` exposes `themeName` via `useAuth()` but not the
 * resolved Fluent `Theme` object itself, so this module re-derives it from
 * the same three theme exports `TeamsAuthProvider` uses internally
 * (`teamsLightTheme` / `teamsDarkTheme` / `teamsHighContrastTheme`).
 */
import { useMemo } from 'react';
import * as echarts from 'echarts/core';
import {
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
  type Theme,
} from '@fluentui/react-components';
import { useAuth } from '../../auth/TeamsAuthProvider';
import type { TeamsThemeName } from '../../../types';

export interface ChartColors {
  inbound: string;
  outbound: string;
  positive: string;
  warning: string;
  negative: string;
  neutral: string;
  emphasis: string;
  muted: string;
  /** Sparkline fill under a hero KPI, per spec §3.1: outbound color at alpha 0.20. */
  sparklineFill: string;
  /** Sequential ramp for heatmaps: `n` stops of the inbound color at rising alpha. */
  ramp: (n: number) => string[];
  /**
   * Categorical palette for an *unordered* set of `n` categories (e.g. a
   * donut's slices, a stacked bar's segments, a multi-line chart's series) --
   * NOT for ordered/magnitude data, which is what `ramp()` is for. Cycles
   * through the theme's genuinely distinct hues (`inbound`, `outbound`,
   * `positive`, `warning`, `negative`, plus three more Fluent palette
   * families -- blue, grape, pink -- reserved solely for this cycle and not
   * reused as any other semantic color in this file) rather than shades of
   * one hue, so adjacent categories stay visually distinguishable and a
   * viewer doesn't read a color gradient as an implied ordering. `neutral` is
   * intentionally excluded from the cycle -- reserved for a caller to assign
   * explicitly to a catch-all bucket (e.g. "Other" / "Unassigned") that is
   * semantically not one of the ranked categories. 8 distinct hues covers
   * every current caller (`CallsByDayChart` caps at top-6 branches + neutral
   * "Other", other queues charts have a bounded but not tiny number of
   * result/action types) without wrapping; it still cycles (repeats from the
   * start) past 8 rather than throwing, for any future caller with more
   * categories than that.
   */
  categorical: (n: number) => string[];
}

const THEME_BY_NAME: Record<TeamsThemeName, Theme> = {
  default: teamsLightTheme,
  dark: teamsDarkTheme,
  contrast: teamsHighContrastTheme,
};

function resolveFluentTheme(themeName: TeamsThemeName): Theme {
  return THEME_BY_NAME[themeName] ?? teamsLightTheme;
}

/**
 * Outbound per spec §3.1: `colorPaletteTealBackground3` when `'default'`,
 * `colorPaletteTealForeground2` when `'dark'` / `'contrast'`.
 *
 * Errata (recorded on PR #52, https://github.com/hemang-shah/Teams-cdr-tabapp/pull/52):
 * `colorPaletteTealBackground3` does not exist in the installed
 * `@fluentui/react-components` -- Teal is a 2-tier palette family
 * (`Background2` / `Foreground2` / `BorderActive` only, no `Background1/3` or
 * `Foreground1/3`), verified against every shipped theme export. The nearest
 * existing token, `colorPaletteTealBackground2`, only reaches 1.57:1 contrast
 * against `colorNeutralBackground1` in the `'default'` theme, failing the
 * spec's own required >= 3:1 bar for graphical objects.
 * `colorPaletteTealBorderActive` (`#038387`) is used instead: it exists, it
 * measures 4.56:1 contrast against `colorNeutralBackground1`, and it is the
 * closest real token to both the approved brand mock (`#0F766E`) and the
 * shipped logo asset color (`frontend/src/assets/brand/voiceiq-mark-light.svg`,
 * `stroke="#0F766E"`). This is a correction of a nonexistent token name, not a
 * change to the approved visual target.
 */
function outboundColor(theme: Theme, themeName: TeamsThemeName): string {
  return themeName === 'default' ? theme.colorPaletteTealBorderActive : theme.colorPaletteTealForeground2;
}

/**
 * Parses a Fluent theme color (`#rgb` or `#rrggbb`) and re-emits it as
 * `rgba()` at `alpha`. Fluent tokens are always 6-digit hex (no alpha
 * channel of their own), so an 8-digit `#rrggbbaa` input is not handled --
 * only the first 6 hex digits are read.
 */
function withAlpha(hex: string, alpha: number): string {
  let clean = hex.replace('#', '');
  if (clean.length === 3) {
    clean = clean.split('').map((c) => c + c).join('');
  }
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Sequential ramp stops per spec §3.1: inbound color at alpha 0.12 / 0.30 / 0.50 / 0.75 / 1.00. */
const RAMP_ALPHAS = [0.12, 0.3, 0.5, 0.75, 1];

function buildRamp(inbound: string, n: number): string[] {
  if (n <= 1) {
    return [withAlpha(inbound, RAMP_ALPHAS[RAMP_ALPHAS.length - 1])];
  }
  if (n === RAMP_ALPHAS.length) {
    return RAMP_ALPHAS.map((alpha) => withAlpha(inbound, alpha));
  }
  // Evenly interpolate `n` stops across the same 0.12-1.00 alpha range for
  // callers that need a different step count than the canonical 5.
  const [minAlpha, maxAlpha] = [RAMP_ALPHAS[0], RAMP_ALPHAS[RAMP_ALPHAS.length - 1]];
  return Array.from({ length: n }, (_, i) => {
    const t = i / (n - 1);
    const alpha = minAlpha + t * (maxAlpha - minAlpha);
    return withAlpha(inbound, Math.round(alpha * 100) / 100);
  });
}

function buildChartColors(theme: Theme, themeName: TeamsThemeName): ChartColors {
  const inbound = theme.colorBrandBackground;
  const outbound = outboundColor(theme, themeName);
  const positive = theme.colorPaletteGreenForeground1;
  const warning = theme.colorPaletteMarigoldForeground1;
  const negative = theme.colorPaletteRedForeground1;
  // Three additional Fluent palette families used ONLY to extend the
  // categorical cycle past the 5 semantic colors above -- not exposed as
  // named `ChartColors` fields, and not reused for any other meaning in this
  // file. `*Foreground2` (not `*Foreground1`) is chosen because that's the
  // same token tier already used for `outbound` in the 'dark'/'contrast'
  // themes (see `outboundColor`), so the added hues stay visually consistent
  // in weight with the rest of the palette across all 3 themes.
  const categoricalExtra1 = theme.colorPaletteBlueForeground2;
  const categoricalExtra2 = theme.colorPaletteGrapeForeground2;
  const categoricalExtra3 = theme.colorPalettePinkForeground2;
  // Fixed cycle order for `categorical()`: brand hue first (most "us"),
  // then the remaining genuinely distinct hues. `neutral` deliberately
  // excluded -- see the `categorical` doc comment on `ChartColors`.
  const categoricalCycle = [
    inbound,
    outbound,
    positive,
    warning,
    negative,
    categoricalExtra1,
    categoricalExtra2,
    categoricalExtra3,
  ];
  return {
    inbound,
    outbound,
    positive,
    warning,
    negative,
    neutral: theme.colorNeutralForeground3,
    emphasis: theme.colorBrandBackground,
    muted: withAlpha(inbound, 0.35),
    sparklineFill: withAlpha(outbound, 0.2),
    ramp: (n: number) => buildRamp(inbound, n),
    categorical: (n: number) =>
      Array.from({ length: Math.max(n, 0) }, (_, i) => categoricalCycle[i % categoricalCycle.length]),
  };
}

/**
 * Semantic color API of spec §4.4/§4.5. Every literal color a chart `option`
 * needs comes from here.
 *
 * Memoized on `themeName` so the returned object (and its `ramp`/`categorical`
 * closures) keeps a stable identity across re-renders where the theme hasn't
 * changed. Every chart consumer depends on this object's identity inside its
 * own `useMemo(() => ({...}), [..., colors])` to decide whether to rebuild its
 * ECharts `option` -- an unstable identity here defeated all of those memos,
 * causing `Chart` to call `setOption(option, { notMerge: true })` on every
 * unrelated re-render, which wholesale-replaced live chart state (e.g.
 * resetting a user-dragged heatmap `visualMap` range).
 */
export function useChartColors(): ChartColors {
  const { themeName } = useAuth();
  return useMemo(() => buildChartColors(resolveFluentTheme(themeName), themeName), [themeName]);
}

const registeredThemeNames = new Set<TeamsThemeName>();

/** Builds the ECharts theme object for a Fluent theme -- palette, background, text, axis/split lines, tooltip chrome. */
function buildEChartsTheme(theme: Theme, themeName: TeamsThemeName): Record<string, unknown> {
  const colors = buildChartColors(theme, themeName);
  const axisLabel = { color: theme.colorNeutralForeground3, fontSize: 11 };

  return {
    color: [colors.inbound, colors.outbound],
    backgroundColor: 'transparent',
    textStyle: { color: theme.colorNeutralForeground1 },
    title: { textStyle: { color: theme.colorNeutralForeground1 } },
    // Built-in legend off by default (spec §4.4/§5). There is no shared
    // legend component -- individual charts that need a visible legend
    // (`branch/BranchDashboard.tsx`, `company/charts/TrendChart.tsx`,
    // `dashboard/MyDashboard.tsx`) each override this with their own
    // `legend: { show: true, ... }` config.
    legend: { show: false, textStyle: { color: theme.colorNeutralForeground3 } },
    tooltip: {
      backgroundColor: theme.colorNeutralBackground1,
      borderColor: theme.colorNeutralStroke2,
      borderWidth: 1,
      textStyle: { color: theme.colorNeutralForeground1, fontSize: 12 },
      extraCssText: `box-shadow: ${theme.shadow4}; border-radius: 6px;`,
    },
    categoryAxis: {
      axisLine: { lineStyle: { color: theme.colorNeutralStroke2 } },
      axisTick: { show: false },
      axisLabel,
      splitLine: { show: false },
    },
    valueAxis: {
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel,
      splitLine: { lineStyle: { color: theme.colorNeutralStroke2, type: 'dashed' } },
    },
  };
}

/**
 * Registers the ECharts theme for `themeName` (idempotent per name) so
 * `Chart` can `echarts.init(el, themeName)`. Called by `Chart` before every
 * init/re-init; safe to call redundantly.
 */
export function ensureChartTheme(themeName: TeamsThemeName): void {
  if (registeredThemeNames.has(themeName)) {
    return;
  }
  const theme = resolveFluentTheme(themeName);
  echarts.registerTheme(themeName, buildEChartsTheme(theme, themeName));
  registeredThemeNames.add(themeName);
}
